import './input-text.scss'

import { throttle } from 'lodash'
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react'
import { Image } from 'react-bootstrap'
import { fitem_t } from 'src/types'
import { join_str } from 'src/utils'

export const InputText: FC<fitem_t> = ({
  id,
  label,
  required,
  disabled,
  capture = '',
  placeholder,
  icon,
  icon_size = 26,
  input_textarea,
  input_type,
  error,
  warning,
  fired,
  uppercase,
  classes,
  classes_field = 'input input--hoshi',
  classes_label = 'input__label input__label--hoshi',
  classes_input = 'pr-4 input__field input__field--hoshi',
  classes_error = 'm-0 ml-1 mt-n2 font-12-semibold c-error text-uppercase',
  classes_icon = 'mr-1 float-right input--icon',
  classes_disabled = 'bg-fafafa cursor-not-allowed',
  set_field_value,
  on_press,
  on_press_icon,
  on_change,
  on_focus_capture,
}: fitem_t) => {
  /**
   * Use Ref
   */
  const throttled = useRef(
    throttle((newval) => set_field_value?.(id, uppercase ? newval?.toUpperCase() : newval), 500)
  )

  /**
   * Use State
   */
  const [value, set_value] = useState(capture)
  const [focus, set_focus] = useState(false)

  /**
   * Use Effect
   */
  useEffect(() => {
    set_value(capture)
  }, [capture])

  useEffect(() => {
    throttled.current(value)
    on_change?.(value)
  }, [value])

  useEffect(() => {
    on_focus_capture?.(focus)
  }, [focus])

  /**
   * Alias
   */
  const end = required && !disabled ? '*' : ''
  const classes_ = {
    label: join_str(classes_label),
    input: join_str(classes_input, disabled ? classes_disabled : ''),
    spanl: join_str('input__label-content input__label-content--hoshi', disabled ? 'c-bfbfbf' : ''),
    iconr: join_str(classes_icon, disabled ? classes_disabled : 'cursor-pointer'),
    field: join_str(classes_field, focus || value ? 'input--filled' : ''),
    error: join_str(classes_error || 'd-none', fired && error ? '' : 'opacity-0'),
    warning: join_str(classes_error || 'd-none', warning && !error ? '' : 'opacity-0'),
  }
  const iprops = {
    id,
    value,
    type: 'text',
    disabled: disabled,
    readOnly: input_type === 'SELECT',
    placeholder: focus ? placeholder : '',
    className: classes_.input,
    onFocusCapture: () => set_focus(true),
    onBlurCapture: () => set_focus(false),
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      set_value(event?.target?.value)
    },
    title: value,
  }

  /**
   * Render functions
   */
  const render_label = () => (
    <label htmlFor={id} className={classes_.label}>
      <span className={classes_.spanl}>{`${label}${end}`}</span>
    </label>
  )

  const render_input = () => {
    return input_textarea ? <textarea {...iprops} /> : <input {...iprops} />
  }

  const render_icon = () => {
    return (
      icon && (
        <Image
          src={icon}
          width={icon_size}
          height={icon_size}
          className={classes_.iconr}
          onClick={() => {
            if (!disabled) on_press_icon?.()
          }}
        />
      )
    )
  }

  const render_field = () => (
    <span className={classes_.field} onClick={on_press}>
      {render_label()}
      {render_input()}
      {render_icon()}
    </span>
  )

  const render_error = () => (
    <p className={classes_.error}>
      {error}
      <span className="opacity-0">-</span>
    </p>
  )

  const render_warning = () => (
    <div className={warning && 'input__warning'}>
      <p className={classes_.warning}>
        {warning}
        <span className="opacity-0">-</span>
      </p>
    </div>
  )

  return (
    <main className={classes}>
      <div className="d-flex flex-column">
        {render_field()}
        {render_error()}
        {render_warning()}
      </div>
    </main>
  )
}
